<template>
  <div class="markets__list">
    <market-item
      v-for="market in marketsIsEnabled"
      :key="market.id"
      :market="market"
      :isChecked="selectedMarkets.includes(market.id)"
      @toggle="toggleMarket"
      :type="type"
      :showBadge="mpsToExport?.includes(market.id)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import MarketItem from "@/components/UI/MarketsCheckbox/MarketItem.vue";
import wbMini from "@/assets/images/wbMini.png";
import ozonMini from "@/assets/images/ozonMini.png";
import yandexMini from "@/assets/images/yandexMini.svg";
import { MarketPlacesEnum } from "@/types/types";
import { usePartner } from "@/stores/partner/partner";
import { createNotification } from "@/tools/notification";

export type MarketCheckbox = {
  id: MarketPlacesEnum;
  name: string;
  img: string;
};

interface Props {
  selectedMarkets: MarketPlacesEnum[];
  type?: "switch" | "checkbox" | "onlyIcon";
  notificationCheckboxOn?: boolean;
  mpsToExport?: MarketPlacesEnum[];
}

const props = withDefaults(defineProps<Props>(), {
  type: "onlyIcon",
  notificationCheckbox: false,
});

const emit = defineEmits<{
  (
    event: "update:selected-markets",
    updatedSelection: MarketPlacesEnum[],
  ): void;
}>();

const partnerStore = usePartner();
const markets = ref<MarketCheckbox[]>([
  {
    id: MarketPlacesEnum.wildberries,
    name: "wildberriesProductCardId",
    img: wbMini,
  },
  {
    id: MarketPlacesEnum.ozon,
    name: "ozonProductId",
    img: ozonMini,
  },
  {
    id: MarketPlacesEnum.yandexMarket,
    name: "yandexProductId",
    img: yandexMini,
  },
]);

const marketsIsEnabled = computed(() => {
  return markets.value.filter((el) => {
    if (el.id === MarketPlacesEnum.wildberries) {
      return partnerStore.partner?.wildberriesEnabled;
    } else if (el.id === MarketPlacesEnum.ozon) {
      return partnerStore.partner?.ozonEnabled;
    } else if (el.id === MarketPlacesEnum.yandexMarket) {
      return partnerStore.partner?.yandexMarketEnabled;
    }
    return false; // Страховка для других случаев
  });
});

function toggleMarket(marketId: MarketPlacesEnum) {
  const updatedSelection = props.selectedMarkets.includes(marketId)
    ? props.selectedMarkets.filter((id) => id !== marketId)
    : [...props.selectedMarkets, marketId];

  emit("update:selected-markets", updatedSelection);
}

watch(
  () => props.selectedMarkets,
  (newSelection, oldSelection) => {
    if (props.notificationCheckboxOn) {
      const addedMarkets = newSelection.filter(
        (id) => !oldSelection.includes(id),
      );
      if (addedMarkets.length > 0) {
        addedMarkets.forEach((marketId) => {
          const market = markets.value.find((market) => market.id === marketId);
          if (market) {
            createNotification(
              "info",
              `Вы активировали выгрузку на ${market.id}, данные будут отправлены на маркетплейс при сохранении`,
            );
          }
        });
      }
    }
  },
  { deep: true },
);
</script>

<style scoped lang="scss">
.markets__list {
  display: flex;
  gap: 10px;
}
</style>
